import { Link } from "react-router-dom";
import { AlertTriangle, CalendarClock, Radio, Sparkles, FileEdit, Rocket } from "lucide-react";
import { Section, StatusBadge } from "@/components/Brand";

export const DEFAULT_WIDGETS = ["status_counts", "stat_protocols", "stat_interventions", "stat_unverified", "stat_events", "review_calendar", "recent_activity"];

const Stat = ({ label, value, accent }) => (
  <div className="card-surface h-full p-4">
    <div className="eyebrow">{label}</div>
    <div className={`mt-1 font-heading text-3xl font-extrabold ${accent || "text-slate-900"}`}>{value ?? "–"}</div>
  </div>
);

const VersionList = ({ rows, empty, testid }) => (rows.length === 0 ? <p className="text-sm text-slate-500">{empty}</p> : (
  <ul className="divide-y divide-slate-100" data-testid={testid}>
    {rows.map((r) => (
      <li key={r.version_id} className="flex items-center gap-3 py-2 text-[13px]">
        <Link to={`/protocols/${r.protocol_id}`} className="min-w-0 flex-1 truncate font-semibold text-slate-800 hover:text-sky-700 hover:underline">{r.title}</Link>
        <StatusBadge status={r.status} />
        <span className="font-mono text-[11px] text-slate-400">v{r.version}{r.at ? ` · ${r.at.slice(0, 10)}` : ""}</span>
      </li>
    ))}
  </ul>
));

const Calendar = ({ d }) => (
  <Section title="Review calendar" testid="review-calendar" right={<CalendarClock size={16} className="text-slate-400" />}>
    {d.overdue.length === 0 ? <p className="text-sm text-slate-500">No overdue clinical reviews.</p> : (
      <ul className="space-y-2">
        {d.overdue.map((o) => (
          <li key={o.version_id} className="flex items-center gap-3 rounded border border-red-200 bg-red-50 px-3 py-2" data-testid={`overdue-${o.protocol_id}`}>
            <AlertTriangle size={16} className="text-red-600 shrink-0" />
            <Link to={`/protocols/${o.protocol_id}`} className="text-sm font-semibold text-red-800 hover:underline">{o.title}</Link>
            <span className="ml-auto font-mono text-[11px] text-red-700">v{o.version} · due {o.next_review_due}</span>
          </li>
        ))}
      </ul>
    )}
    <p className="mt-3 text-[12px] text-slate-500">{d.due_within_year} published version(s) due for review within 12 months. Overdue protocols block new assignments when the governance setting is on.</p>
  </Section>
);

const Activity = ({ d }) => (
  <Section title="Recent activity" testid="recent-activity" right={<Radio size={16} className="text-slate-400" />}>
    <ul className="divide-y divide-slate-100">
      {d.recent.map((a) => (
        <li key={a.id} className="py-2 flex items-center gap-3 text-[13px]">
          <span className="font-mono text-[11px] text-slate-400 w-32 shrink-0">{a.ts.slice(0, 16).replace("T", " ")}</span>
          <span className="font-mono text-[12px] text-sky-700">{a.action}</span>
          <span className="ml-auto truncate text-slate-500">{a.actor_email || a.actor_role}</span>
        </li>
      ))}
    </ul>
    <Link to="/audit" className="mt-2 inline-block text-[12px] font-semibold text-sky-700 hover:underline" data-testid="view-audit-link">View full audit trail →</Link>
  </Section>
);

const StatusCounts = ({ d }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
    {Object.entries(d.counts).map(([k, v]) => (
      <div key={k} className="card-surface p-4" data-testid={`stat-${k}`}>
        <StatusBadge status={k} testid={`stat-badge-${k}`} />
        <div className="mt-2 font-heading text-3xl font-extrabold text-slate-900">{v}</div>
        <div className="text-[11px] text-slate-500">versions</div>
      </div>
    ))}
  </div>
);

// size: 1 = quarter row, 2 = half row, 4 = full row (on large screens)
export const WIDGETS = {
  status_counts: { label: "Versions by status", size: 4, render: (d) => <StatusCounts d={d} /> },
  stat_protocols: { label: "Protocols", size: 1, render: (d) => <div data-testid="stat-protocols" className="h-full"><Stat label="Protocols" value={d.protocols} /></div> },
  stat_interventions: { label: "Interventions in library", size: 1, render: (d) => <div data-testid="stat-interventions" className="h-full"><Stat label="Interventions in library" value={d.interventions} /></div> },
  stat_unverified: { label: "Unverified illustrations", size: 1, render: (d) => <div data-testid="stat-unverified" className="h-full"><Stat label="Unverified illustrations" value={d.illustrations_unverified} accent="text-amber-600" /></div> },
  stat_events: { label: "Events emitted", size: 1, render: (d) => <div data-testid="stat-events" className="h-full"><Stat label="Events emitted" value={d.events} /></div> },
  review_calendar: { label: "Review calendar", size: 2, render: (d) => <Calendar d={d} /> },
  recent_activity: { label: "Recent activity", size: 2, render: (d) => <Activity d={d} /> },
  ai_unreviewed: { label: "Published by PranIQ AI – not reviewed", size: 2, icon: Sparkles,
    render: (d) => <Section title="Published by PranIQ AI · not clinically reviewed" right={<Sparkles size={16} className="text-violet-500" />}><VersionList rows={d.ai_unreviewed} empty="Nothing waiting – every published protocol has been reviewed." testid="ai-unreviewed-list" /></Section> },
  my_drafts: { label: "My drafts", size: 2, icon: FileEdit,
    render: (d) => <Section title="My drafts & reviews" right={<FileEdit size={16} className="text-slate-400" />}><VersionList rows={d.my_drafts} empty="You have no drafts in progress." testid="my-drafts-list" /></Section> },
  recently_published: { label: "Recently published", size: 2, icon: Rocket,
    render: (d) => <Section title="Recently published" right={<Rocket size={16} className="text-slate-400" />}><VersionList rows={d.recently_published} empty="Nothing published yet." testid="recently-published-list" /></Section> },
};
export const SPAN = { 1: "col-span-2 md:col-span-1", 2: "col-span-2 lg:col-span-2", 4: "col-span-2 lg:col-span-4" };
